<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.WEB.5 — call button in the conversation header, for
// WhatsApp Cloud inboxes with calling enabled. It places an outbound call
// (API.4); when Kaption holds no valid permission for the contact, it offers
// to send the call permission request instead (API.5 / SCOPE.2).
//
// whatsapp-calling-ownership.UI.5 / OUTBOUND.1..3, .5 — what the button says
// is the SERVER's decision (GET .../kaption_calls/eligibility):
//   assignee · callback · admin → Call (straight away, assignment unchanged)
//   unassigned                  → Assign me and call (the confirm says so)
//   anyone_in_inbox             → Call; the confirm says it assigns it to you
//   not_assignee                → "Assigned to <name>" + "Ask <name>" (a
//                                 private note mentioning them — never a
//                                 dead button)
// While it loads: assignee / unassigned / admin (the ACCOUNT role,
// getCurrentRole) decide; anyone else sees a loading Call, never "Ask".
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useKaptionCallStore } from './kaptionCallStore';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const calls = useKaptionCallStore();

const ASK_TEAMMATE_EVENT = 'kaption-call-ask-teammate';

const inbox = computed(() =>
  store.getters['inboxes/getInbox'](props.conversation?.inbox_id)
);
const contact = computed(() => props.conversation?.meta?.sender || {});
const currentUser = computed(() => store.getters.getCurrentUser || {});
// The account role: the user object's own `role` can be another account's.
const isAdmin = computed(
  () => store.getters.getCurrentRole === 'administrator'
);
const assignee = computed(() => props.conversation?.meta?.assignee || null);

const isWhatsappCloud = computed(
  () =>
    inbox.value?.channel_type === 'Channel::Whatsapp' &&
    (inbox.value?.provider || 'whatsapp_cloud') === 'whatsapp_cloud'
);

const isVisible = computed(
  () =>
    isKaptionFlagEnabled('KAPTION_WHATSAPP_CALLING') &&
    isKaptionFlagEnabled('KAPTION_WHATSAPP_CALLING_OUTBOUND') &&
    isWhatsappCloud.value &&
    calls.isInboxCallingEnabled(inbox.value)
);

const firstName = name => (name || '').split(' ')[0] || '';

// whatsapp-calling-ownership.OUTBOUND.5
const eligibility = ref(null);
let eligibilityRequest = 0;
const loadEligibility = async () => {
  eligibility.value = null;
  if (!isVisible.value || !props.conversation?.id) return;
  eligibilityRequest += 1;
  const request = eligibilityRequest;
  const result = await calls.loadOutboundEligibility(props.conversation.id);
  if (request === eligibilityRequest) eligibility.value = result;
};
// A finished call can make me its callback agent; a reassignment changes it.
watch(
  () => [
    isVisible.value,
    props.conversation?.id,
    assignee.value?.id,
    calls.state.activeCallId,
  ],
  loadEligibility,
  { immediate: true }
);

const MODE_BY_REASON = {
  assignee: 'call',
  callback: 'call',
  admin: 'call',
  unassigned: 'assign_and_call',
  anyone_in_inbox: 'call_reassigns',
  not_assignee: 'assigned_other',
};

// call | call_reassigns | assign_and_call | assigned_other | checking
const mode = computed(() => {
  const decided = MODE_BY_REASON[eligibility.value?.reason];
  if (decided) return decided;
  const mine = assignee.value?.id && assignee.value.id === currentUser.value.id;
  if (mine) return 'call';
  if (!assignee.value?.id) return 'assign_and_call';
  return isAdmin.value ? 'call' : 'checking';
});

const askPerson = computed(
  () => eligibility.value?.assignee || assignee.value || null
);

const isStarting = ref(false);
const isRequesting = ref(false);
const permissionDialog = ref(null);
const confirmDialog = ref(null);

const startCall = async () => {
  if (calls.state.activeCallId) {
    useAlert(t('KAPTION_CALLS.HEADER.ALREADY_ON_CALL'));
    return;
  }
  isStarting.value = true;
  const result = await calls.startOutbound(props.conversation.id, {
    name: contact.value.name,
    thumbnail: contact.value.thumbnail,
    inboxName: inbox.value?.name,
  });
  isStarting.value = false;
  confirmDialog.value?.close();
  if (result === 'permission_required') permissionDialog.value?.open();
  if (result?.status === 'not_assignee') {
    eligibility.value = {
      can_call: false,
      reason: 'not_assignee',
      assigns: false,
      assignee: result.assignee || assignee.value,
    };
    useAlert(
      t('KAPTION_CALLS.HEADER.NOT_ASSIGNEE', {
        name: result.assignee?.name || '',
      })
    );
  }
};

const onClick = () => {
  if (mode.value === 'call' || mode.value === 'checking') {
    startCall();
    return;
  }
  confirmDialog.value?.open();
};

const confirmCall = () => startCall();

const confirmCopy = computed(() => {
  if (mode.value === 'call_reassigns')
    return {
      title: t('KAPTION_CALLS.HEADER.TAKE_OVER_TITLE'),
      body: t('KAPTION_CALLS.HEADER.TAKE_OVER_BODY', {
        name: assignee.value?.name || '',
      }),
    };
  return {
    title: t('KAPTION_CALLS.HEADER.ASSIGN_TITLE'),
    body: t('KAPTION_CALLS.HEADER.ASSIGN_BODY', {
      name: contact.value.name || '',
    }),
  };
});

// OUTBOUND.3 — "Ask <name>": a private note that mentions the assignee.
const askAssignee = () => {
  const person = askPerson.value;
  if (!person?.id) return;
  const mention = `[@${person.name}](mention://user/${person.id}/${encodeURIComponent(person.name || '')})`;
  emitter.emit(ASK_TEAMMATE_EVENT, {
    conversationId: props.conversation.id,
    content: `${mention} ${t('KAPTION_CALLS.HEADER.ASK_NOTE')}`,
  });
};

const sendPermissionRequest = async () => {
  isRequesting.value = true;
  await calls.requestPermission(props.conversation.id);
  isRequesting.value = false;
  permissionDialog.value?.close();
};
</script>

<template>
  <div v-if="isVisible" class="contents">
    <Button
      v-if="mode === 'call' || mode === 'call_reassigns' || mode === 'checking'"
      v-tooltip.bottom="$t('KAPTION_CALLS.HEADER.CALL_TOOLTIP')"
      data-testid="kaption-call-button"
      :data-mode="mode"
      icon="i-lucide-phone"
      variant="ghost"
      color="slate"
      size="sm"
      :aria-label="$t('KAPTION_CALLS.HEADER.CALL_TOOLTIP')"
      :is-loading="isStarting || mode === 'checking'"
      :disabled="mode === 'checking'"
      @click="onClick"
    />
    <Button
      v-else-if="mode === 'assign_and_call'"
      data-testid="kaption-call-button"
      :data-mode="mode"
      icon="i-lucide-phone"
      :label="$t('KAPTION_CALLS.HEADER.ASSIGN_AND_CALL')"
      variant="faded"
      color="slate"
      size="sm"
      :is-loading="isStarting"
      @click="onClick"
    />
    <span
      v-else
      data-testid="kaption-call-assigned-other"
      class="flex items-center gap-1.5"
    >
      <span
        v-tooltip.bottom="
          $t('KAPTION_CALLS.HEADER.ASSIGNEE_CALLS', {
            name: firstName(askPerson?.name),
          })
        "
        class="hidden items-center gap-1 text-xs text-n-slate-11 lg:flex"
      >
        <span class="i-lucide-phone size-3.5" />
        {{ $t('KAPTION_CALLS.HEADER.ASSIGNED_TO', { name: askPerson?.name }) }}
      </span>
      <Button
        data-testid="kaption-call-ask"
        :label="
          $t('KAPTION_CALLS.HEADER.ASK', { name: firstName(askPerson?.name) })
        "
        variant="faded"
        color="slate"
        size="sm"
        @click="askAssignee"
      />
    </span>
    <Dialog
      ref="confirmDialog"
      type="edit"
      :title="confirmCopy.title"
      :description="confirmCopy.body"
      :confirm-button-label="$t('KAPTION_CALLS.HEADER.CONFIRM_CALL')"
      :cancel-button-label="$t('KAPTION_CALLS.HEADER.CANCEL')"
      :is-loading="isStarting"
      @confirm="confirmCall"
    />
    <Dialog
      ref="permissionDialog"
      type="edit"
      :title="$t('KAPTION_CALLS.HEADER.PERMISSION_TITLE')"
      :description="
        $t('KAPTION_CALLS.HEADER.PERMISSION_BODY', {
          name: contact.name || '',
        })
      "
      :confirm-button-label="$t('KAPTION_CALLS.HEADER.SEND_REQUEST')"
      :cancel-button-label="$t('KAPTION_CALLS.HEADER.CANCEL')"
      :is-loading="isRequesting"
      @confirm="sendPermissionRequest"
    />
  </div>
</template>
